import { useEffect, useRef, useState } from 'react';

/**
 * Tweens a number toward `target` on requestAnimationFrame, so a figure counts
 * up into place instead of snapping to its final value.
 *
 * Two details that matter:
 *
 *  - The final frame is written explicitly. Easing that merely APPROACHES 1
 *    leaves the readout short of the real number, which on a financial figure
 *    means displaying a value that is simply wrong.
 *  - `shown` seeds the starting point but is deliberately absent from the
 *    dependency list. Including it would restart the tween on every frame it
 *    sets, so the value would creep and never arrive.
 *
 * Honours prefers-reduced-motion by jumping straight to the target.
 *
 * @param {number} target    value to land on
 * @param {number} duration  milliseconds (default 900)
 * @returns {number}         the current frame's value
 */
const useCountUp = (target, duration = 900) => {
  const [shown, setShown] = useState(0);
  const frameRef = useRef(0);

  useEffect(() => {
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduced || !Number.isFinite(target)) {
      setShown(Number.isFinite(target) ? target : 0);
      return undefined;
    }

    const from = shown;
    const start = performance.now();

    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      if (t >= 1) {
        setShown(target);
        return;
      }
      // easeOutCubic, close enough to --mx-ease-glide's deceleration that a
      // number and a bar or ring animating together read as one movement.
      const eased = 1 - Math.pow(1 - t, 3);
      setShown(from + (target - from) * eased);
      frameRef.current = requestAnimationFrame(tick);
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, duration]);

  return shown;
};

export default useCountUp;
